import type { BrandAssetHash } from "@ascleo/frontdesk-contracts/branding/model";
import { createDcAuthHeaders } from "../lib/auth-headers";
import type { BrandingTransport } from "./branding-types";

const MAX_BRANDING_ASSET_BYTES = 5 * 1024 * 1024;

/**
 * Fetch implementation accepted by the shared HTTP branding transport.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const fetcher: BrandingFetch = globalThis.fetch;
 * ```
 */
export type BrandingFetch = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

/**
 * Configuration for a credential-free or signed branding HTTP transport.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const config = { endpoint: new URL("/api/frontdesk/branding/", location.origin) };
 * ```
 */
export interface BrandingHttpTransportOptions {
  readonly endpoint: URL;
  readonly fetcher?: BrandingFetch;
  readonly credentials?: RequestCredentials;
  readonly createHeaders?: () => Promise<Headers>;
}

function childUrl(endpoint: URL, child: string): URL {
  const base = endpoint.href.endsWith("/")
    ? endpoint
    : new URL(`${endpoint.href}/`);
  return new URL(child, base);
}

async function request(
  options: BrandingHttpTransportOptions,
  child: string,
  signal: AbortSignal,
): Promise<Response> {
  const headers = options.createHeaders
    ? await options.createHeaders()
    : new Headers();
  headers.set("Accept", "application/json");
  return (options.fetcher ?? globalThis.fetch)(childUrl(options.endpoint, child), {
    method: "GET",
    cache: "no-store",
    credentials: options.credentials,
    headers,
    signal,
  });
}

async function readJson(
  options: BrandingHttpTransportOptions,
  child: string,
  signal: AbortSignal,
): Promise<unknown> {
  const response = await request(options, child, signal);
  if (!response.ok) {
    throw new Error(`Branding request failed with status ${response.status}.`);
  }
  return response.json();
}

/**
 * Create the bounded terminal-facing branding transport for one HTTP endpoint.
 *
 * JSON remains unknown for the shared runtime ingress decoder. Binary responses become the exact
 * cache-asset envelope and are bounded before and after reading.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const transport = makeBrandingHttpTransport({ endpoint });
 * ```
 */
export function makeBrandingHttpTransport(
  options: BrandingHttpTransportOptions,
): BrandingTransport {
  return {
    fetchManifest: (signal) => readJson(options, "manifest", signal),
    waitForChange: async (afterVersion, signal) => {
      const response = await request(
        options,
        `changes?afterVersion=${encodeURIComponent(afterVersion)}`,
        signal,
      );
      if (response.status === 503) return { outcome: "unavailable" };
      if (!response.ok) {
        throw new Error(`Branding wait failed with status ${response.status}.`);
      }
      return response.json();
    },
    fetchAsset: async (hash: BrandAssetHash, signal) => {
      const response = await request(options, `assets/${hash}`, signal);
      if (!response.ok) {
        throw new Error(`Branding asset request failed with status ${response.status}.`);
      }

      const declaredLengthHeader = response.headers.get("content-length");
      const declaredLength =
        declaredLengthHeader === null ? undefined : Number(declaredLengthHeader);
      if (
        declaredLength !== undefined &&
        (!Number.isFinite(declaredLength) ||
          declaredLength <= 0 ||
          declaredLength > MAX_BRANDING_ASSET_BYTES)
      ) {
        throw new Error("Branding asset length is outside the supported bound.");
      }
      const bytes = new Uint8Array(await response.arrayBuffer());
      if (bytes.byteLength === 0 || bytes.byteLength > MAX_BRANDING_ASSET_BYTES) {
        throw new Error("Branding asset body is outside the supported bound.");
      }
      return {
        metadata: {
          assetHash: hash,
          mediaType: response.headers.get("content-type"),
          byteLength: bytes.byteLength,
        },
        bytes,
      };
    },
  };
}

/**
 * Resolve the public branding endpoint below an inSuite `_rest_` root.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * brandingEndpointFromRestUrl("https://practice.example/sol/frontdeskplus/_rest_");
 * ```
 */
export function brandingEndpointFromRestUrl(insuiteRestUrl: string): URL {
  const root = insuiteRestUrl.endsWith("/")
    ? insuiteRestUrl
    : `${insuiteRestUrl}/`;
  return new URL("api/frontdesk/branding/", root);
}

/**
 * Create a direct Bridge transport using the canonical SOLTOKEN signature headers.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const transport = makeSignedBrandingTransport(restUrl, solToken);
 * ```
 */
export function makeSignedBrandingTransport(
  insuiteRestUrl: string,
  solToken: string,
  fetcher?: BrandingFetch,
): BrandingTransport {
  return makeBrandingHttpTransport({
    endpoint: brandingEndpointFromRestUrl(insuiteRestUrl),
    fetcher,
    createHeaders: () => createDcAuthHeaders(solToken),
  });
}

/**
 * Derive a stable non-secret cache namespace from an authority identifier.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const namespace = await brandingAuthorityFingerprint(restUrl);
 * ```
 */
export async function brandingAuthorityFingerprint(
  authority: string,
): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(authority.trim()),
  );
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}
